import { inject, Injectable } from '@angular/core';
import { CanActivate, GuardResult, MaybeAsync, Router } from '@angular/router';
import { SplashService } from '@context/frontend/common';
import { hasInheritedRolePermission } from '@context/shared/types/user';
import { Subject, takeUntil } from 'rxjs';
import { SuperAuthService } from '../services';

/**
 * Waits for SuperAuthService to be ready (authenticated + user loaded).
 * Use with SuperAdminGuard for role enforcement.
 */
@Injectable({ providedIn: 'root' })
export class CustomSuperAuthGuard implements CanActivate {
	private readonly superAuth = inject(SuperAuthService);
	private readonly router = inject(Router);
	private readonly splash = inject(SplashService);

	canActivate(): MaybeAsync<GuardResult> {
		this.splash.onShow();
		const resolved$ = new Subject<boolean>();
		return new Promise<boolean>((resolve) => {
			this.superAuth.authenticated$
				.pipe(takeUntil(resolved$))
				.subscribe((authenticated) => {
					if (!authenticated) return;

					const user = this.superAuth.authUser();
					if (!user?.role) return;

					const hasSuperPermission = hasInheritedRolePermission(
						user.role,
						'super',
					);

					resolve(hasSuperPermission);
					resolved$.next(true);
				});
		}).then((canActivate) => {
			this.splash.onHide();
			if (!canActivate) return this.denyAccess();
			return canActivate;
		});
	}

	private readonly denyAccess = (): Promise<boolean> =>
		this.superAuth.signOut().then(() => {
			const url = this.router.serializeUrl(
				this.router.createUrlTree(['auth'], {
					queryParams: { accessDenied: 'true' },
				}),
			);
			window.location.href = url;
			return false;
		});
}
